import {
  signUp,
  confirmSignUp,
  resendSignUpCode,
  signIn,
  getCurrentUser,
} from 'aws-amplify/auth';
import { createUser, cleanupUnconfirmedSignup } from '@/api/imperative/members';
import { persistLocalSession } from '@/features/auth/lib/session';

const PENDING_SIGNUP_KEY = 'mpassist_pending_signup';

function errorText(error) {
  return `${error?.name || error?.code || ''} ${error?.message || ''}`;
}

function isUsernameExistsError(error) {
  return /UsernameExistsException|user.*already\s*exists/i.test(errorText(error));
}

function isAlreadyConfirmedError(error) {
  return /already\s*confirmed|Current status is CONFIRMED|confirmed\s*and\s*active/i.test(
    errorText(error)
  );
}

/** Persist non-secret signup fields only (never password). */
export function savePendingSignup({ name, email, orgName }) {
  sessionStorage.setItem(
    PENDING_SIGNUP_KEY,
    JSON.stringify({ name, email, orgName })
  );
}

export function loadPendingSignup() {
  try {
    const raw = sessionStorage.getItem(PENDING_SIGNUP_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function clearPendingSignup() {
  sessionStorage.removeItem(PENDING_SIGNUP_KEY);
}

async function signUpWithPassword({ name, email, password }) {
  const result = await signUp({
    username: email,
    password,
    options: {
      userAttributes: { email, name },
    },
  });
  return {
    needsConfirmation: result.nextStep?.signUpStep === 'CONFIRM_SIGN_UP',
  };
}

/** @returns {{ needsConfirmation: boolean }} */
export async function registerWithCognito({ name, email, password }) {
  try {
    return await signUpWithPassword({ name, email, password });
  } catch (error) {
    if (!isUsernameExistsError(error)) throw error;

    // Unconfirmed leftover — delete on backend, then sign up fresh with new password
    const cleaned = await cleanupUnconfirmedSignup(email);
    if (cleaned === false) {
      const confirmed = new Error('User account already exists');
      confirmed.name = 'UserAlreadyConfirmed';
      throw confirmed;
    }
    if (cleaned == null) {
      throw new Error('Could not reset incomplete signup. Please try again.');
    }

    return signUpWithPassword({ name, email, password });
  }
}

export async function confirmRegistration({ email, code }) {
  try {
    return await confirmSignUp({
      username: email,
      confirmationCode: code,
    });
  } catch (error) {
    if (isAlreadyConfirmedError(error)) return { alreadyConfirmed: true };
    throw error;
  }
}

export async function resendRegistrationCode(email) {
  return resendSignUpCode({ username: email });
}

async function ensureSignedIn({ email, password }) {
  try {
    await getCurrentUser();
    return;
  } catch {
    // not signed in
  }
  if (!password) {
    const err = new Error('Password required to sign in');
    err.name = 'PasswordRequired';
    throw err;
  }
  await signIn({ username: email, password });
}

/** Confirm done → sign in → create org/user → persist session. */
export async function completeSignupAfterConfirm({ name, email, password, orgName }) {
  await ensureSignedIn({ email, password });
  const userId = await createUser({ name, orgName });
  persistLocalSession({ userId, email });
  clearPendingSignup();
  return userId;
}
